@if (loading) {
  <div>
    <h3 class="panel-title"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</h3>
  </div>
} @else {
  @if (launching) {
    <div class="flex-center-row light-content-panel">
      <h3 class="panel-title">
        <i class="fa-solid fa-spinner fa-spin"></i> Just a second, we are launching your recovery.
      </h3>
    </div>
  }
  @if (!launching) {
    <div class="flex-left-column light-control-host">
      <div class="modal-header">
        <h3 class="panel-title">Launch recovery for "{{ !!application ? application.name : '' }}"</h3>
        <button
          type="button"
          class="modal-close-btn"
          (click)="onCancelClick()"
          pTooltip="Close"
          tooltipPosition="left"
          aria-label="Close"
        >
          <i class="fa-utility fa-semibold fa-xmark"></i>
        </button>
      </div>
      <div class="modal-subtitle">
        Select applications to recover to {{ !!application ? application.targetRegion : '' }}
      </div>
      <div
        id="modal-step-app-recovery"
        class="modal-step modal-step--collapsible"
        [class.is-open]="isStepOpen('app-recovery')"
      >
        <div
          class="modal-step-label"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="isStepOpen('app-recovery')"
          aria-controls="modal-step-app-recovery-body"
          (click)="toggleStep('app-recovery')"
          (keydown)="onStepHeaderKeydown($event, 'app-recovery')"
        >
          <span class="modal-step-number"></span>
          <span class="modal-step-title">Applications &amp; recovery point</span>
          @if (totalEligibleApplicationsCount > 0) {
            <div class="modal-step-actions">
              <span class="selection-count">
                <strong>{{ selectedApplications.length }}</strong> of {{ totalEligibleApplicationsCount }} selected
              </span>
              @if (recoveryPointTime) {
                <span class="step-summary-divider">·</span>
              }
              @if (recoveryPointTime) {
                <span class="recovery-point-summary">
                  <strong>{{ recoveryPointTime }}</strong>
                </span>
              }
            </div>
          }
          <i class="fa-solid fa-chevron-down modal-step-chevron"></i>
        </div>
        @if (isStepOpen('app-recovery')) {
          <div id="modal-step-app-recovery-body" class="modal-step-body">
            <div class="modal-step-section">
              <h4>Applications</h4>
              <p class="modal-body-description">
                Select the applications to recover together. All selected apps will restore to the same recovery point.
              </p>
              @if (totalEligibleApplicationsCount > 5) {
                <div class="app-search-wrap">
                  <i class="fa-solid fa-magnifying-glass app-search-icon"></i>
                  <input
                    type="text"
                    class="app-search-input"
                    placeholder="Search applications..."
                    [(ngModel)]="applicationSearchFilter"
                    aria-label="Search applications"
                  />
                  @if (applicationSearchFilter) {
                    <button
                      type="button"
                      class="app-search-clear"
                      (click)="applicationSearchFilter = ''"
                      aria-label="Clear search"
                    >
                      <i class="fa-utility fa-semibold fa-xmark"></i>
                    </button>
                  }
                </div>
              }
              @if (totalEligibleApplicationsCount === 0) {
                <div class="app-selection-empty">
                  <i class="fa-solid fa-circle-info"></i>
                  <span>No other applications available to recover with this one.</span>
                </div>
              }
              @if (totalEligibleApplicationsCount > 0 && applications.length === 0) {
                <div class="app-selection-empty">
                  <i class="fa-solid fa-circle-info"></i>
                  <span>No applications match "{{ applicationSearchFilter }}".</span>
                </div>
              }
              @if (applications.length > 0) {
                <div class="app-list-toolbar">
                  <button
                    type="button"
                    class="select-all-link"
                    (click)="toggleSelectAllApplications(!areAllApplicationsSelected())"
                  >
                    {{ areAllApplicationsSelected() ? 'Deselect all' : 'Select all' }}
                  </button>
                </div>
              }
              @if (applications.length > 0) {
                <div class="app-selection-list" id="applicationSelectionList">
                  @for (application of applications; track applicationTrackBy($index, application)) {
                    <ng-container *ngLet="computeApplicationRecoveryPoint(application.applicationObj) as appRp">
                      <label
                        class="app-selection-row"
                        [class.selected]="application.selected"
                        [class.has-error]="
                          application.selected &&
                          !appRp &&
                          !isRefreshingApplicationRecoveryPoints(application.applicationObj)
                        "
                      >
                        <input
                          class="arpio-checkbox"
                          type="checkbox"
                          (input)="toggleApplicationSelection(application.applicationObj.applicationId)"
                          [checked]="applicationIsChecked(application.applicationObj)"
                        />
                        <span class="app-name">{{ application.applicationObj.name }}</span>
                        @if (application.selected) {
                          @if (appRp) {
                            @if (showLock(appRp)) {
                              <i class="fa-utility fa-semibold fa-lock"></i>
                            }
                            <span class="recovery-timestamp-text">
                              {{ recoversToTimeString(application.applicationObj, appRp) }}
                            </span>
                          } @else {
                            @if (!isRefreshingApplicationRecoveryPoints(application.applicationObj)) {
                              <span class="recovery-timestamp-error">No matching recovery points</span>
                            }
                          }
                        }
                      </label>
                    </ng-container>
                  }
                </div>
              }
            </div>
            <div class="modal-step-section">
              <h4>Recovery point</h4>
              <p class="modal-body-description">
                Pick the moment in time to restore from. Every selected application recovers to this exact point — apps
                above update live as you slide so you can see whose recovery point is at risk.
              </p>
              @if (showRetainedRpsCheckbox) {
                <label class="retained-rps-toggle">
                  <input
                    id="only-protected-rps"
                    class="arpio-checkbox"
                    type="checkbox"
                    (input)="computeApplicationsRecoveryPoint()"
                    [(ngModel)]="onlyProtectedRps"
                  />
                  <span class="retained-rps-label">
                    Restrict to indefinitely-retained Recovery Points
                    <i class="fa-utility fa-semibold fa-lock"></i>
                  </span>
                </label>
              }
              @if (canAdjustRecoveryPoint) {
                <rp-range-slider
                  class="full-width"
                  [recoveryPoints]="recoveryPoints"
                  [enableRealTimeRecovery]="enableRealTimeRecovery"
                  (update)="recoveryPointChanged($event)"
                >
                </rp-range-slider>
              } @else {
                <div class="rp-locked-banner">
                  <i class="fa-utility fa-semibold fa-lock"></i>
                  <span
                    >Recovery Point locked at <strong>{{ recoveryPointTime }}</strong></span
                  >
                </div>
              }
              <recovery-time-info
                [realTime]="selectedIsRealTime"
                [snapshotTime]="recoveryPointSnapshotTime"
              ></recovery-time-info>
            </div>
            <div class="modal-step-continue-row">
              <button type="button" class="arpio-secondary-button" (click)="continueToStep(nextStepAfterAppRecovery)">
                CONTINUE
              </button>
            </div>
          </div>
        }
      </div>
      @if (accountService.quarantineEnabled) {
        <div
          id="modal-step-quarantine"
          class="modal-step modal-step--collapsible"
          [class.is-open]="isStepOpen('quarantine')"
        >
          <div
            class="modal-step-label"
            role="button"
            tabindex="0"
            [attr.aria-expanded]="isStepOpen('quarantine')"
            aria-controls="modal-step-quarantine-body"
            (click)="toggleStep('quarantine')"
            (keydown)="onStepHeaderKeydown($event, 'quarantine')"
          >
            <span class="modal-step-number"></span>
            <span class="modal-step-title">Quarantine mode</span>
            <div class="modal-step-actions">
              <span>{{ quarantineRecoveryEnabled ? 'On' : 'Off' }}</span>
            </div>
            <i class="fa-solid fa-chevron-down modal-step-chevron"></i>
          </div>
          @if (isStepOpen('quarantine')) {
            <div id="modal-step-quarantine-body" class="modal-step-body">
              <p class="modal-body-description">
                Recover {{ quarantineInstanceNoun }} into an isolated network so you can inspect them for threats before
                releasing to production traffic.
              </p>
              <div class="sandbox-toggle">
                <input
                  id="quarantine-recovery-enabled"
                  class="arpio-checkbox"
                  type="checkbox"
                  (input)="toggleQuarantineRecovery($event)"
                  [checked]="quarantineRecoveryEnabled"
                />
                <div class="sandbox-toggle-content">
                  <label for="quarantine-recovery-enabled">
                    <h4>Enable Quarantine Mode for Ransomware Recovery</h4>
                  </label>
                  <!-- `&nbsp;` before "Learn more." — don't strip; keeps the link bonded to the sentence. -->
                  <div class="modal-subtitle">
                    With Quarantine Mode enabled, {{ quarantineInstanceNoun }} recover into an isolated state. You can
                    release them individually from quarantine in the recovery environment.&nbsp;<a
                      href="{{ uiService.ransomwareDocsUrl }}"
                      target="_blank"
                      >Learn more.</a
                    >
                  </div>
                </div>
              </div>
              @if (accountService.entitledForCapability('malwareScan') && !isAzure) {
                <div
                  class="flex-child-indent sandbox-toggle margin-top"
                  [class.modal-section-disabled]="!quarantineRecoveryEnabled"
                >
                  <span
                    [pTooltip]="
                      !quarantineRecoveryEnabled
                        ? 'Enable Quarantine Mode above to run a GuardDuty malware scan on recovered instances.'
                        : null
                    "
                    tooltipPosition="top"
                  >
                    <input
                      class="arpio-checkbox slight-margin-top"
                      type="checkbox"
                      id="run-scan"
                      (input)="toggleMalwareScan($event)"
                      [checked]="malwareScanEnabled"
                      [disabled]="!quarantineRecoveryEnabled"
                    />
                  </span>
                  <div class="sandbox-toggle-content">
                    <label for="run-scan">Run GuardDuty Malware Scan on Quarantined Instances</label>
                    <div class="modal-subtitle">
                      With scanning enabled, GuardDuty inspects each quarantined instance for malware. Review findings
                      before releasing instances from quarantine.
                    </div>
                  </div>
                </div>
              }
              <div class="modal-step-continue-row">
                <button type="button" class="arpio-secondary-button" (click)="continueToStep('review')">
                  CONTINUE
                </button>
              </div>
            </div>
          }
        </div>
      }
      <div id="modal-step-review" class="modal-step modal-step--collapsible" [class.is-open]="isStepOpen('review')">
        <div
          class="modal-step-label"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="isStepOpen('review')"
          aria-controls="modal-step-review-body"
          (click)="toggleStep('review')"
          (keydown)="onStepHeaderKeydown($event, 'review')"
        >
          <span class="modal-step-number"></span>
          <span class="modal-step-title">Review</span>
          <i class="fa-solid fa-chevron-down modal-step-chevron"></i>
        </div>
        @if (isStepOpen('review')) {
          <div id="modal-step-review-body" class="modal-step-body">
            <p class="modal-body-description">
              Confirm the settings below, then choose <strong>LAUNCH RECOVERY</strong> to launch.
            </p>
            @if (appsMissingRecoveryPoint.length > 0) {
              <div class="notice-warning review-warning">
                <i class="fa-utility fa-semibold fa-circle-exclamation"></i>
                <p>
                  <strong>{{ appsMissingRecoveryPoint.length }}</strong> selected
                  {{ appsMissingRecoveryPoint.length === 1 ? 'application has' : 'applications have' }} no recovery
                  point at this time. Pick a more recent point or deselect them to continue.
                </p>
              </div>
            }
            <h4 class="review-heading">Applications</h4>
            @if (selectedApplications.length > 0) {
              <ul class="review-list">
                @for (app of selectedApplications; track app) {
                  <ng-container *ngLet="computeApplicationRecoveryPoint(app) as appRp">
                    <li class="review-row" [class.has-error]="!appRp">
                      <i
                        class="fa-utility fa-semibold review-row-icon"
                        [class.fa-circle-check]="!!appRp"
                        [class.fa-circle-exclamation]="!appRp"
                      ></i>
                      <span class="app-name">{{ app.name }}</span>
                      @if (appRp) {
                        @if (showLock(appRp)) {
                          <i class="fa-utility fa-semibold fa-lock"></i>
                        }
                        <span class="recovery-timestamp-text">{{ recoversToTimeString(app, appRp) }}</span>
                      } @else {
                        <span class="recovery-timestamp-error">No matching recovery point</span>
                      }
                    </li>
                  </ng-container>
                }
              </ul>
            } @else {
              <div class="app-selection-empty">
                <i class="fa-solid fa-circle-info"></i>
                <span>No applications selected.</span>
              </div>
            }
            @if (accountService.quarantineEnabled) {
              <h4 class="review-heading">Quarantine mode</h4>
              <p class="review-line">
                <strong>{{ quarantineRecoveryEnabled ? 'On' : 'Off' }}</strong>
                @if (quarantineRecoveryEnabled && accountService.entitledForCapability('malwareScan') && !isAzure) {
                  <span class="review-line-detail">
                    — GuardDuty malware scan {{ malwareScanEnabled ? 'enabled' : 'disabled' }}
                  </span>
                }
              </p>
            }
          </div>
        }
      </div>
      <div class="modal-footer">
        <button class="arpio-secondary-button" (click)="onCancelClick()" [disabled]="launching">CANCEL</button>
        <button
          busy-button
          [busy]="launching"
          id="failover-button"
          class="arpio-button"
          (click)="onLaunchClick()"
          [disabled]="!canLaunch"
        >
          LAUNCH RECOVERY
        </button>
      </div>
    </div>
  }
}
